import { FormValue, Invitation } from "@modules/settings/types";
import { validateEmailFormat } from "@common/utils";
import { FC, useState, useEffect, useMemo } from "react";
import { CgSpinner } from "react-icons/cg";
import { Button, Typography } from "@earned/core";
import Input from "@common/components/Input";
import { FieldError } from "react-hook-form";

interface FamilyMembersProps {
  data: Invitation[];
  onSubmit: (emails: FormValue[]) => void;
  coClients: Forme.Client[];
  isLoading?: boolean;
}

const FamilyMembers: FC<FamilyMembersProps> = ({
  data,
  coClients,
  onSubmit,
  isLoading,
}) => {
  const [familyMembers, setFamilyMembers] = useState<Invitation[]>([]);
  const [formValues, setFormValues] = useState<FormValue[]>([]);

  const sendInvites = () => {
    if (validateForm()) {
      setFormValues([]);
      onSubmit(formValues);
    }
  };

  const addFormInput = () => {
    setFormValues([...formValues, { key: `${formValues.length}`, value: "" }]);
  };

  const resetValues = () => {
    setFormValues([]);
  };

  const onChangeValue = (index: string, value?: string, error?: string) => {
    const values = [...formValues];
    if (value) {
      values[index as unknown as number].value = value;
    }
    if (error) {
      values[index as unknown as number].error = error;
    }
    if (!error) {
      values[index as unknown as number].error = "";
    }
    values[index as unknown as number].key = index;
    setFormValues(values);
  };

  const validateForm = () => {
    const validatedEmails = formValues.map(
      ({ value }) => !!validateEmailFormat(value)
    );

    validatedEmails.map((isValid, index) => {
      if (!isValid) {
        onChangeValue(String(index), undefined, "Email format is invalid");
      }
    });
    return !validatedEmails.includes(false);
  };

  const isAbleToAddInvitation = useMemo(() => {
    return formValues.length <= 5;
  }, [formValues]);

  useEffect(() => {
    setFamilyMembers(data);
  }, [data]);

  return (
    <div>
      {coClients.length != 0 &&
        coClients.map((coClient) => (
          <div
            key={`${coClient.id}`}
            className="border-b border-primary first:border-t"
          >
            <div className="my-3 flex flex-row justify-between px-3">
              <Typography variant="body" color="primary" weight="medium">
                {coClient.auth0UserEmail}
              </Typography>
              <Typography variant="body" color="secondary">
                Co-Client
              </Typography>
            </div>
          </div>
        ))}
      {familyMembers.map((member) => (
        <div
          key={`${member.invitedByUserId}-${member.invitedUserId}`}
          className="border-b border-primary first:border-t"
        >
          <div className="my-3 flex flex-row justify-between px-3">
            <Typography variant="body" color="primary" weight="medium">
              {member.email}
            </Typography>
            <Typography variant="body" color="secondary">
              {member.status}
            </Typography>
          </div>
        </div>
      ))}
      {!!formValues?.length && (
        <div className="my-4 flex flex-col space-y-2">
          {formValues?.map((item, index) => (
            <>
              <Input
                key={item.key}
                placeholder="Enter email address"
                error={
                  item.error
                    ? ({ message: item.error } as FieldError)
                    : undefined
                }
                onChange={(event) =>
                  onChangeValue(String(index), event.target.value)
                }
              />
            </>
          ))}
        </div>
      )}
      {isLoading && <CgSpinner className="animate-spin" size={20} />}
      {isAbleToAddInvitation && (
        <div className="-mr-4 flex flex-row items-end justify-end">
          <Button
            variant="tertiary"
            onClick={() => addFormInput()}
            disabled={isLoading}
          >
            + Add another
          </Button>
        </div>
      )}
      {!!formValues?.length && (
        <div className="mt-8 flex flex-row justify-end space-x-2">
          <Button variant="secondary" onClick={resetValues}>
            Cancel
          </Button>
          <Button onClick={sendInvites} variant="primary" disabled={isLoading}>
            Send Invite
          </Button>
        </div>
      )}
    </div>
  );
};

export default FamilyMembers;
